<template>
  <BillingFeatureGate feature="price_request_form"
                      variant="page"
                      :title="$t('priceRequestFormTitle')"
                      :description="$t('priceRequestFormDescription')">
    <div>
      <Toolbar class="p-mb-4 p-mt-4 p-p-2">
        <template #start>
          <div style="font-size: 1.4em; color: inherit" class="p-p-2">
            {{ $t('priceRequestFormTitle') }}
          </div>
        </template>
        <template #end>
          <div class="p-p-1">
            <Button @click="saveSettings"
                    :disabled="!canUsePriceRequestForm || !$store.getters.isAdminOrHigher || locationIsLoading || fetchInFlight"
                    :loading="locationIsLoading || fetchInFlight"
                    class="p-button-raised"
                    icon="ti-save"
                    :label="$t('Save')"/>
          </div>
        </template>
      </Toolbar>

      <div class="p-grid price-request-settings">
        <div class="p-col-12 p-lg-4 p-mb-2">
          <fieldset class="p-fieldset p-pb-4 settings-fieldset">
            <legend class="p-fieldset-legend p-p-2">{{ $t('priceRequestAvailabilityTitle') }}</legend>

            <p class="p-text-secondary p-mt-2 p-mb-4">
              {{ $t('priceRequestFormDescription') }}
            </p>

            <div class="p-d-flex p-ai-center p-mt-3">
              <div class="p-d-flex p-ai-center">
                <InputSwitch id="price-request-enabled-checkbox"
                             :modelValue="canUsePriceRequestForm && priceRequestEnabled"
                             @update:modelValue="priceRequestEnabled = $event"
                             :disabled="!canUsePriceRequestForm"/>
              </div>
              <label for="price-request-enabled-checkbox"
                     class="switch-label"
                     :class="{ 'pointer': canUsePriceRequestForm }">
                {{ $t('acceptPriceRequestsForLocationLabel') }}
              </label>
            </div>

            <small v-if="canUsePriceRequestForm" class="p-d-block p-mt-3 p-text-secondary">
              {{ $t('priceRequestAvailabilityHelp') }}
            </small>

            <div v-if="canUsePriceRequestForm && !priceRequestEnabled"
                 class="price-request-notice p-mt-4">
              {{ $t('priceRequestDisabledHint') }}
            </div>
          </fieldset>
        </div>

        <div class="p-col-12 p-lg-8 p-mb-2">
          <fieldset class="p-fieldset p-pb-4 settings-fieldset">
            <legend class="p-fieldset-legend p-p-2">{{ $t('priceRequestLinksTitle') }}</legend>

            <div class="p-mt-5 p-mb-4 language-selector">
              <div class="p-float-label">
                <CustomDropdown id="price-request-language"
                                class="settings-input"
                                v-model="selectedFormLanguage"
                                :options="formLanguageOptions"
                                data-key="value"
                                :showClear="false">
                  <template #value="slotProps">
                    <span v-if="slotProps.value">{{ slotProps.value.name }}</span>
                    <span v-else style="visibility: hidden">.</span>
                  </template>
                  <template #option="slotProps">
                    <span>{{ slotProps.option.name }}</span>
                  </template>
                </CustomDropdown>
                <label for="price-request-language">{{ $t('priceRequestLanguageLabel') }}</label>
              </div>
              <small class="p-d-block p-mt-2 p-text-secondary">
                {{ $t('priceRequestLanguageSelectorHelp') }}
              </small>
            </div>

            <div class="p-fieldset p-p-3 p-mb-3 link-card">
              <div class="link-card__content">
                <div class="link-card__title">{{ $t('priceRequestCompanyFormTitle') }}</div>
                <div class="p-text-secondary p-mt-1 p-mb-2">
                  {{ $t('priceRequestCompanyFormHelp') }}
                </div>
                <a v-if="priceRequestTenantUrl"
                   :href="priceRequestTenantUrl"
                   class="price-request-link"
                   :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                   rel="noopener noreferrer">
<!--                  {{ priceRequestTenantUrl }}-->
                  {{ displayPriceRequestUrl(priceRequestTenantUrl) }}
                </a>
              </div>
              <div class="link-card__actions">
                <Button :label="$t('Copy')"
                        icon="ti-files"
                        class="p-button-text p-button-sm"
                        :disabled="!priceRequestTenantUrl"
                        @click="copyText(displayPriceRequestUrl(priceRequestTenantUrl))"/>
                <Button :label="$t('openInNewTabLabel')"
                        icon="ti-new-window"
                        class="p-button-text p-button-sm p-button-secondary"
                        :disabled="!priceRequestTenantUrl"
                        @click="openUrl(priceRequestTenantUrl)"/>
              </div>
            </div>

            <div class="p-fieldset p-p-3 link-card">
              <div class="link-card__content">
                <div class="link-card__title">{{ $t('priceRequestLocationFormTitle') }}</div>
                <div class="p-text-secondary p-mt-1 p-mb-2">
                  {{ $t('priceRequestLocationFormHelp', { location: locationName }) }}
                </div>
                <a v-if="priceRequestLocationUrl"
                   :href="priceRequestLocationUrl"
                   class="price-request-link"
                   :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                   rel="noopener noreferrer">
<!--                  {{ priceRequestLocationUrl }}-->
<!--                  {{ displayPriceRequestUrl(priceRequestTenantUrl) }}-->
                  {{ displayPriceRequestUrl(priceRequestLocationUrl) }}
                </a>
              </div>
              <div class="link-card__actions">
                <Button :label="$t('Copy')"
                        icon="ti-files"
                        class="p-button-text p-button-sm"
                        :disabled="!priceRequestLocationUrl"
                        @click="copyText(displayPriceRequestUrl(priceRequestLocationUrl))"/>
                <Button :label="$t('openInNewTabLabel')"
                        icon="ti-new-window"
                        class="p-button-text p-button-sm p-button-secondary"
                        :disabled="!priceRequestLocationUrl"
                        @click="openUrl(priceRequestLocationUrl)"/>
              </div>
            </div>
          </fieldset>
        </div>

        <div class="p-col-12">
          <fieldset class="p-fieldset p-pb-4 settings-fieldset">
            <legend class="p-fieldset-legend p-p-2">{{ $t('priceRequestWebsiteTitle') }}</legend>

            <p class="p-text-secondary p-mt-2 p-mb-4">
              {{ $t('priceRequestWebsiteDescription') }}
            </p>

            <div class="p-d-flex p-ai-center p-flex-wrap p-mb-3">
              <div class="p-field-radiobutton p-mr-4 p-mb-2">
                <RadioButton id="price-request-target-company"
                             value="tenant"
                             v-model="snippetTarget"/>
                <label for="price-request-target-company" class="pointer">
                  {{ $t('priceRequestCompanyFormTitle') }}
                </label>
              </div>
              <div class="p-field-radiobutton p-mb-2">
                <RadioButton id="price-request-target-location"
                             value="location"
                             v-model="snippetTarget"/>
                <label for="price-request-target-location" class="pointer">
                  {{ $t('priceRequestLocationFormTitle') }}<template v-if="locationName"> ({{ locationName }})</template>
                </label>
              </div>
            </div>

            <div class="p-fieldset p-p-0 p-mt-3 p-mb-4 code-block">
              <pre><code v-text="htmlSnippet"></code></pre>
              <Button :label="$t('copyHtmlCodeLabel')"
                      icon="ti-files"
                      class="p-button-text p-button-sm code-block__copy"
                      :disabled="!snippetUrl"
                      @click="copyText(htmlSnippet)"/>
            </div>


            <!--        <div class="code-block">-->
            <!--          <pre><code v-text="htmlSnippet"></code></pre>-->
            <!--          <Button :label="$t('copyHtmlCodeLabel')"-->
            <!--                  icon="ti-files"-->
            <!--                  class="p-button-text p-button-sm code-block__copy"-->
            <!--                  :disabled="!snippetUrl"-->
            <!--                  @click="copyText(htmlSnippet)"/>-->
            <!--        </div>-->

            <small class="p-d-block p-mt-3 p-text-secondary">
              {{ $t('priceRequestWebsiteHint') }}
            </small>
          </fieldset>
        </div>
      </div>
    </div>
  </BillingFeatureGate>
</template>

<script>
import httpClient from '@/services/http.services'
import settings from '@/settings'
import showErrorsMixins from '@/mixins/showErrorsMixins'
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import BillingFeatureGate from '@/components/billing/BillingFeatureGate.vue'

export default {
  components: { BillingFeatureGate },
  emits: ['update-items'],
  mixins: [showErrorsMixins, billingFeaturesMixins],
  props: {
    locationData: {
      type: Object,
      default: null,
    },
    locationIsLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      priceRequestEnabled: false,
      selectedFormLanguage: null,
      snippetTarget: 'tenant',
      fetchInFlight: false,
      settings,
    }
  },
  computed: {
    canUsePriceRequestForm() {
      return this.locationData?.price_request_form_available === true
    },
    formLanguageOptions() {
      return (settings.languages || []).map(language => ({
        name: `${String(language.value || '').toUpperCase()} — ${language.label}`,
        value: language.value,
        label: language.label,
      }))
    },
    formLanguage() {
      return this.selectedFormLanguage?.value
          || this.locationData?.language
          || settings.fallbackLanguage
          || 'en'
    },
    locationName() {
      return this.locationData?.public_name || this.locationData?.name || ''
    },
    priceRequestTenantUrl() {
      const tenantSlug = this.locationData?.tenant_slug
      if (!tenantSlug) return ''

      return `${this.appBaseUrl}/quote/${tenantSlug}/${this.formLanguage}`
    },
    priceRequestLocationUrl() {
      const tenantSlug = this.locationData?.tenant_slug
      const locationSlug = this.locationData?.slug
      if (!tenantSlug || !locationSlug) return ''

      return `${this.appBaseUrl}/quote/${tenantSlug}/${locationSlug}/${this.formLanguage}`
    },
    appBaseUrl() {
      return String(settings.appBaseUrl || '').replace(/\/$/, '')
    },
    snippetUrl() {
      return this.snippetTarget === 'location'
          ? this.priceRequestLocationUrl
          : this.priceRequestTenantUrl
    },
    snippetLinkText() {
      return this.$t('priceRequestLinkText', this.formLanguage)
    },
    htmlSnippet() {
      if (!this.snippetUrl) return ''

      return `<a href="${this.displayPriceRequestUrl(this.snippetUrl)}"\n` +
          `   target="_blank"\n` +
          `   rel="noopener noreferrer">\n` +
          `  ${this.snippetLinkText}\n` +
          `</a>`
    },
  },
  watch: {
    locationData: {
      deep: true,
      handler() {
        this.applySettings()
      },
    },
  },
  mounted() {
    this.applySettings()
  },
  methods: {
    applySettings() {
      this.priceRequestEnabled = !!+this.locationData?.is_price_request_enabled

      const language = this.selectedFormLanguage?.value
          || this.locationData?.language
          || settings.fallbackLanguage
          || 'en'

      this.selectedFormLanguage = this.formLanguageOptions.find(option => option.value === language)
          || this.formLanguageOptions[0]
          || null
    },
    async saveSettings() {
      const locationId = this.locationData?.id
      if (!locationId || !this.canUsePriceRequestForm || this.fetchInFlight || !this.$store.getters.isAdminOrHigher) return

      this.fetchInFlight = true

      try {
        const { data, status } = await httpClient.post('location/settings-update', {
          location_id: locationId,
          is_price_request_enabled: this.priceRequestEnabled ? 1 : 0,
        })

        if (status === 200 && data?.success) {
          this.$emit('update-items', locationId)
          this.$toast.add({ severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife })
        } else {
          this.showUnknownError()
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.fetchInFlight = false
      }
    },
    async copyText(text) {
      if (!text) return

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = text
          textarea.setAttribute('readonly', '')
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }

        this.$toast.add({
          severity: 'success',
          detail: this.$t('priceRequestCopiedMessage'),
          life: settings.toastLife,
        })
      } catch (err) {
        this.showError(err)
      }
    },
    openUrl(url) {
      if (!url) return
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    displayPriceRequestUrl(url) {
      return url.replace(this.appBaseUrl, 'https://app.virtualgarage.app')
    },
  },
}
</script>

<style scoped lang="scss">
.price-request-settings,
.price-request-settings > [class*='p-col'],
.settings-fieldset {
  min-width: 0;
}

//.settings-fieldset {
//  height: 100%;
//}

.settings-input {
  width: 100%;
  max-width: 320px;
}

.language-selector {
  max-width: 420px;
}

.price-request-notice {
  padding: 0.75rem 1rem;
  border-left: 3px solid var(--orange-400, #f59e0b);
  //background: var(--surface-card, transparent);
  color: var(--text-color-secondary, #6c757d);
  border-radius: 3px;
}

.link-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.link-card__content {
  min-width: 0;
  flex: 1 1 auto;
}

.link-card__title {
  font-weight: 600;
}

.link-card__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 0.5rem;
}

.price-request-link {
  display: inline-block;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.code-block {
  position: relative;
  //border: 1px solid var(--surface-border, #dee2e6);
  //border-radius: 4px;
  //background: var(--surface-card, transparent);
  //color: var(--text-color);
  overflow: hidden;
  //background: var(--surface-card, transparent);
  background: color-mix(in srgb, var(--text-color) 5%, transparent);
}

.code-block pre {
  margin: 0;
  padding: 1rem 11rem 1rem 1rem;
  overflow-x: auto;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: var(--text-color);
}

.code-block pre code {
  color: var(--text-color);
  //background: transparent;
}

.code-block__copy {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
}

@media (max-width: 768px) {
  .link-card {
    align-items: stretch;
    flex-direction: column;
  }

  .link-card__actions {
    flex-wrap: wrap;
  }

  .code-block pre {
    padding: 4.75rem 1rem 1rem;
  }

  .code-block__copy {
    left: 0.75rem;
    right: auto;
  }
}
</style>
